import { useEffect, useState } from "react";
import { ListChecks } from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField, ActionBar, Button, StatusBadge } from "@fawkes/ui";

import { formatDate, formatDateTime, todayIso } from "../i18n/format";

// De stappen van één uitvaart: het spoor in de dossierkop en de tegel Handelingen.
//
// De lijst komt van de api, die hem uitrekent uit het schema van de uitvaartvorm — zie het scherm
// Handelingen. Er is dus niets om hier te "laden en bewaren": afvinken en heropenen zijn de enige
// twee schrijfacties, en na elke actie haalt het scherm de stand opnieuw op.
//
// Urls RELATIEF — de dataProvider zet er één keer /api voor.

/** Een te kiezen uitvoerder. `loginName` gaat naar de api, `naam` staat op het scherm. */
interface Medewerker {
  loginName: string;
  naam: string;
}

export interface DossierStap {
  dossierId: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  handelingId: string;
  handeling: string;
  verplicht: boolean;
  document: string | null;
  verwachtOp: string | null;
  /** Het ingeplande tijdstip op die dag, als de balie er een koos. */
  verwachtTijd: string | null;
  uitgevoerdOp: string | null;
  uitgevoerdDoor: string | null;
  opmerking: string | null;
}

/** Haalt de stappen van één dossier op; het detailscherm deelt ze met de kop en de tegel. */
export function useDossierStappen(dossierId: string) {
  const { data, isLoading, isError, refetch } = useCustom<DossierStap[]>({
    url: `crematorium/dossiers/${dossierId}/handelingen`,
    method: "get",
  });
  // Defensief: een endpoint dat (nog) geen lijst teruggeeft mag het dossierscherm niet slopen.
  const stappen = Array.isArray(data?.data) ? (data.data as DossierStap[]) : [];
  return { stappen, isLoading, isError, refetch };
}

/** De eerstvolgende stap die nog open staat, of niets als alles af is. */
export function volgendeStap(stappen: DossierStap[]): DossierStap | null {
  return stappen.find((s) => s.uitgevoerdOp == null) ?? null;
}

/**
 * Het paneel in de dossierkop: waar staat dit dossier, en wat is het eerstvolgende dat er moet
 * gebeuren. Het spoor eronder is puur een streepje per stap — genoeg om te zien hoe ver het is.
 */
export function VolgendeStapPaneel({
  stappen,
  onAfgerond,
}: {
  stappen: DossierStap[];
  onAfgerond: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: bezig } = useCustomMutation();

  const volgende = volgendeStap(stappen);
  const afgerond = stappen.filter((s) => s.uitgevoerdOp != null).length;

  const rondAf = () => {
    if (!volgende) return;
    mutate(
      {
        url: `crematorium/dossiers/${volgende.dossierId}/handelingen/${volgende.handelingId}/afronden`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onAfgerond() },
    );
  };

  return (
    <aside className="crem-stap">
      <span className="crem-stap__label">{t("crematieStappen.volgende")}</span>
      <span className="crem-stap__naam">
        {volgende ? volgende.handeling : t("crematieStappen.allesAf")}
      </span>
      <span className="crem-stap__spoor" aria-hidden="true">
        {stappen.map((s) => (
          <span
            key={s.handelingId}
            className={`crem-stap__streep${s.uitgevoerdOp ? " crem-stap__streep--done" : ""}`}
          />
        ))}
      </span>
      <span className="crem-stap__meta">
        {t("crematieStappen.voortgang", { args: [afgerond, stappen.length] })}
        {volgende?.verwachtOp ? ` · ${formatDate(volgende.verwachtOp, locale)}` : ""}
      </span>
      {volgende && (
        <Button className="crem-stap__knop" disabled={bezig} onClick={rondAf}>
          {t("crematieStappen.afronden", { args: [volgende.handeling] })}
        </Button>
      )}
    </aside>
  );
}

/**
 * De tegel Handelingen (mockup): het hele spoor, afgerond én open, en per stap wie, welke dag en
 * welk tijdstip. Een open stap plan je hier in — dag en tijd zijn invulvelden — en vink je af met
 * Afronden; een afgeronde toont wanneer en door wie, en is te heropenen. Wat je inplant gaat pas
 * naar de server bij Opslaan, per tegel, zoals de andere tegels.
 */
export function HandelingenTegel({
  stappen,
  isLoading,
  onGewijzigd,
}: {
  stappen: DossierStap[];
  isLoading: boolean;
  onGewijzigd: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const vandaag = todayIso();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [planning, setPlanning] = useState<Record<string, { datum: string; tijd: string }>>({});
  const [notice, setNotice] = useState<"forbidden" | "error" | null>(null);
  // Wie er te kiezen is als uitvoerder. Een eigen, smal endpoint: /api/users zit achter de
  // gebruikersbeheer-rol en zou hier een 403 en een lege lijst opleveren.
  const { data: medewerkerData } = useCustom<Medewerker[]>({
    url: "crematorium/medewerkers",
    method: "get",
    config: { query: {} },
  });
  // Uitdrukkelijk op vorm gecontroleerd en niet alleen op aanwezigheid: een keuzelijst mag nooit
  // het hele dossierscherm meenemen omdat een endpoint iets anders teruggaf dan verwacht.
  const medewerkers = Array.isArray(medewerkerData?.data) ? medewerkerData.data : [];
  // Wie de stap heeft gedaan, per stap. Leeg = de ingelogde gebruiker; dat is precies wat de api
  // invult als het scherm niets meestuurt, dus die betekenis blijft gelijk.
  const [door, setDoor] = useState<Record<string, string>>({});

  // Na een herlading begint de tegel weer bij wat er staat; anders blijft oude invoer hangen.
  useEffect(() => {
    setPlanning({});
    setDoor({});
    setNotice(null);
  }, [stappen]);

  const waardeVan = (stap: DossierStap) =>
    planning[stap.handelingId] ?? { datum: stap.verwachtOp ?? "", tijd: (stap.verwachtTijd ?? "").slice(0, 5) };
  const zetPlanning = (stap: DossierStap, veld: "datum" | "tijd", waarde: string) =>
    setPlanning((pl) => ({ ...pl, [stap.handelingId]: { ...waardeVan(stap), [veld]: waarde } }));
  const gewijzigd = Object.keys(planning).length > 0;

  const fout = (err: unknown) => {
    const code = (err as { statusCode?: number }).statusCode;
    setNotice(code === 403 ? "forbidden" : "error");
  };

  const zet = (stap: DossierStap, afronden: boolean) => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${stap.dossierId}/handelingen/${stap.handelingId}/afronden`,
        method: afronden ? "post" : "delete",
        // Alleen bij afronden, en alleen als er iemand gekozen is: leeg laten betekent "ikzelf",
        // en dat mag de api zelf invullen.
        values: afronden && door[stap.handelingId] ? { uitgevoerdDoor: door[stap.handelingId] } : {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onGewijzigd(), onError: fout },
    );
  };

  /** Elke gewijzigde stap gaat apart naar de server; als de laatste terug is, laadt het scherm. */
  const opslaan = () => {
    setNotice(null);
    const sleutels = Object.keys(planning);
    let open = sleutels.length;
    const klaar = () => { open -= 1; if (open === 0) onGewijzigd(); };
    for (const handelingId of sleutels) {
      const stap = stappen.find((x) => x.handelingId === handelingId);
      if (!stap) { klaar(); continue; }
      const w = planning[handelingId];
      mutate(
        {
          url: `crematorium/dossiers/${stap.dossierId}/handelingen/${handelingId}/planning`,
          method: "put",
          values: { verwachtOp: w.datum || null, verwachtTijd: w.tijd || null },
          successNotification: false,
          errorNotification: false,
        },
        { onSuccess: klaar, onError: (err) => { fout(err); klaar(); } },
      );
    }
  };

  const afgerond = stappen.filter((s) => s.uitgevoerdOp != null).length;

  return (
    <section className="fui-tile crem-tile crem-tile--handeling">
      <header className="fui-tile__header">
        <h4 className="fui-tile__title">
          <span className="fawkes-relatie-tiletitel">
            <ListChecks size={18} aria-hidden="true" />
            {t("crematieStappen.tegel.titel")}
          </span>
        </h4>
        <div className="fui-tile__actions">
          <span className="fui-muted">
            {t("crematieStappen.voortgang", { args: [afgerond, stappen.length] })}
          </span>
        </div>
      </header>

      <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
        <section className="fawkes-relatie-subcard">
          {notice && (
            <div className="asiel-tarief__notice asiel-tarief__notice--error">
              {t(`crematoriumTarief.notice.${notice}`)}
            </div>
          )}
          <div className="crem-steps">
            {isLoading && <p className="fui-muted">{t("common.loading")}</p>}
            {!isLoading && stappen.length === 0 && (
              <p className="fui-muted">{t("crematieStappen.leeg")}</p>
            )}
            {stappen.map((stap) => {
              const klaar = stap.uitgevoerdOp != null;
              const teLaat = !klaar && stap.verwachtOp != null && stap.verwachtOp < vandaag;
              const w = waardeVan(stap);
              const basis = `h-${stap.handelingId}`;
              return (
                <div key={stap.handelingId} className={`crem-step${klaar ? " crem-step--done" : ""}`}>
                  <span className="crem-step__dot" aria-hidden="true" />
                  <div>
                    <p className="crem-step__name">
                      {stap.handeling}{" "}
                      {klaar ? (
                        <StatusBadge tone="success">
                          {t("crematieStappen.afgerondOp", {
                            args: [formatDateTime(stap.uitgevoerdOp as string, locale)],
                          })}
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone={teLaat ? "danger" : "neutral"}>
                          {stap.verwachtOp
                            ? t(teLaat ? "crematieStappen.teLaatSinds" : "crematieStappen.verwacht",
                                { args: [formatDate(stap.verwachtOp, locale)] })
                            : t("crematoriumWerklijst.termijn.onbepaald")}
                        </StatusBadge>
                      )}
                    </p>
                    <div className="crem-step__fields">
                      <div className="crem-field">
                        <label htmlFor={`${basis}-door`}>{t("crematieStappen.door")}</label>
                        {/* Afgerond: dan staat vast wie het deed. Nog niet afgerond: dan kies je het,
                            want vaak vinkt één iemand af wat een collega heeft gedaan. */}
                        {klaar ? (
                          <input id={`${basis}-door`} className="fui-field__input" readOnly
                            value={stap.uitgevoerdDoor ?? "—"} />
                        ) : (
                          <select id={`${basis}-door`} className="fui-field__input"
                            value={door[stap.handelingId] ?? ""}
                            onChange={(e) =>
                              setDoor((d) => ({ ...d, [stap.handelingId]: e.target.value }))}>
                            <option value="">{t("crematieStappen.doorIkzelf")}</option>
                            {medewerkers.map((m) => (
                              <option key={m.loginName} value={m.loginName}>{m.naam}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="crem-field">
                        <label htmlFor={`${basis}-datum`}>{t("crematieStappen.datum")}</label>
                        <DateField id={`${basis}-datum`} className="fui-field__input" value={klaar ? (stap.uitgevoerdOp as string).slice(0, 10) : w.datum}
                          readOnly={klaar}
                          onChange={(e) => zetPlanning(stap, "datum", e.target.value)} />
                      </div>
                      <div className="crem-field">
                        <label htmlFor={`${basis}-tijd`}>{t("crematieStappen.tijd")}</label>
                        <input id={`${basis}-tijd`} className="fui-field__input" type="time"
                          value={klaar ? (stap.uitgevoerdOp as string).slice(11, 16) : w.tijd}
                          readOnly={klaar}
                          onChange={(e) => zetPlanning(stap, "tijd", e.target.value)} />
                      </div>
                      <Button variant="secondary" disabled={bezig} onClick={() => zet(stap, !klaar)}>
                        {t(klaar ? "crematieStappen.heropenen" : "crematoriumWerklijst.action.afronden")}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <ActionBar>
        <Button variant="secondary" disabled={bezig || !gewijzigd} onClick={() => setPlanning({})}>
          {t("common.cancel")}
        </Button>
        <Button disabled={bezig || !gewijzigd} onClick={opslaan}>
          {t("common.save")}
        </Button>
      </ActionBar>
    </section>
  );
}
